/**
 * 工作台页面
 * 展示各模块概览数据
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Statistic, Spin, Table, Tag, Button, Modal, Descriptions, Space, message, Divider, Result } from 'antd';
import {
  ToolOutlined, ShoppingCartOutlined, SwapOutlined, AppstoreOutlined,
  DollarOutlined, ExclamationCircleOutlined, EyeOutlined, StopOutlined,
} from '@ant-design/icons';
import { repairApi, salesApi, returnApi, partsApi } from '../services/api';

const statusColorMap = {
  pending: 'default', received: 'blue', quoting: 'orange', paid: 'cyan',
  repairing: 'processing', to_ship: 'gold', shipped: 'green', completed: 'success',
  cancelled: 'error', unpaid: 'default',
};

const statusTextMap = {
  pending: '待接修', received: '已接修', quoting: '待付款', paid: '已付款',
  repairing: '维修中', to_ship: '待发货', shipped: '已发货', completed: '已完成',
  cancelled: '已取消', unpaid: '未收款',
};

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ repairs: 0, sales: 0, returns: 0, parts: 0, alerts: 0 });
  const [recentRepairs, setRecentRepairs] = useState([]);
  const [role, setRole] = useState('admin');
  const navigate = useNavigate();

  // 客户操作：详情/付款/放弃维修弹窗
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [repairDetailData, setRepairDetailData] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [abandonModalOpen, setAbandonModalOpen] = useState(false);
  const [abandonLoading, setAbandonLoading] = useState(false);
  const [abandonReason, setAbandonReason] = useState('');

  useEffect(() => {
    // 读取角色信息
    try {
      const info = JSON.parse(localStorage.getItem('admin_info') || '{}');
      setRole(info.role || 'admin');
    } catch { }

    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // 读取当前角色判断是否客户
      let info = {};
      try { info = JSON.parse(localStorage.getItem('admin_info') || '{}'); } catch { }
      const isCustomer = info.role === 'customer';

      if (isCustomer) {
        // 客户只看自己的维修工单（后端自动根据角色隔离数据）
        const repairRes = await repairApi.list({ pageSize: 20 });
        setStats({
          repairs: repairRes.data?.pagination?.total || 0,
          sales: 0, returns: 0, parts: 0, alerts: 0,
        });
        setRecentRepairs(repairRes.data?.list || []);
      } else {
        const [repairRes, salesRes, returnRes, partsRes, alertRes] = await Promise.all([
          repairApi.list({ pageSize: 5 }),
          salesApi.list({ pageSize: 5 }),
          returnApi.list({ pageSize: 5 }),
          partsApi.list({ pageSize: 100 }),
          partsApi.stockAlert(),
        ]);

        setStats({
          repairs: repairRes.data?.pagination?.total || 0,
          sales: salesRes.data?.pagination?.total || 0,
          returns: returnRes.data?.pagination?.total || 0,
          parts: partsRes.data?.pagination?.total || 0,
          alerts: alertRes.data?.alertCount || 0,
        });

        setRecentRepairs(repairRes.data?.list || []);
      }
    } catch (err) {
      console.error('加载仪表盘数据失败', err);
    } finally {
      setLoading(false);
    }
  };

  // ==================== 客户操作：查看详情 ====================
  const openDetail = async (record) => {
    setSelectedRepair(record);
    setDetailLoading(true);
    setDetailModalOpen(true);
    try {
      const res = await repairApi.detail(record.id);
      setRepairDetailData(res.data);
    } catch {
      setRepairDetailData(record);
    } finally {
      setDetailLoading(false);
    }
  };

  // ==================== 客户操作：付款（正式收款已接入通联线上支付，模拟支付关闭） ====================
  // 客户请用微信打开「嘉宇服务平台」小程序，进入「我的 → 维修工单」完成付款
  const handlePay = () => {
    setPayModalOpen(false);
    message.info('请用微信打开「嘉宇服务平台」小程序，进入「我的 → 维修工单」点击去支付');
  };

  // ==================== 客户操作：放弃维修 ====================
  const handleAbandon = async () => {
    if (!selectedRepair) return;
    setAbandonLoading(true);
    try {
      const res = await repairApi.adminAbandon(selectedRepair.id, abandonReason);
      message.success(res.data?.message || '已提交放弃维修申请');
      setAbandonModalOpen(false);
      setDetailModalOpen(false);
      setAbandonReason('');
      loadData();
    } catch (err) {
      // 错误已由拦截器处理
    } finally {
      setAbandonLoading(false);
    }
  };

  const statCards = [
    { title: '维修工单', value: stats.repairs, icon: <ToolOutlined />, color: '#1677ff', path: '/repair/list' },
    { title: '销售订单', value: stats.sales, icon: <ShoppingCartOutlined />, color: '#52c41a', path: '/sales/list' },
    { title: '返修工单', value: stats.returns, icon: <SwapOutlined />, color: '#faad14', path: '/return/list' },
    { title: '库存预警', value: stats.alerts, icon: <AppstoreOutlined />, color: '#ff4d4f', path: '/parts/list' },
  ];

  const columns = [
    { title: '单号', dataIndex: 'repair_no', key: 'repair_no', width: 160 },
    { title: '客户', dataIndex: 'customer_name', key: 'customer_name' },
    { title: '电话', dataIndex: 'phone', key: 'phone' },
    {
      title: '状态', dataIndex: 'status', key: 'status', render: (s) => (
        <Tag color={statusColorMap[s]}>{statusTextMap[s] || s}</Tag>
      ),
    },
    { title: '金额', dataIndex: 'total_fee', key: 'total_fee', render: (v) => v ? `¥${v}` : '-' },
  ];

  // 客户专用列（含操作按钮）
  const customerColumns = [
    { title: '单号', dataIndex: 'repair_no', key: 'repair_no', width: 150 },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 90,
      render: (s) => <Tag color={statusColorMap[s]}>{statusTextMap[s] || s}</Tag>,
    },
    {
      title: '故障描述', dataIndex: 'fault_desc', key: 'fault_desc', ellipsis: true,
      render: (v) => v || '-',
    },
    {
      title: '金额', dataIndex: 'total_fee', key: 'total_fee', width: 100,
      render: (v) => v ? `¥${parseFloat(v).toFixed(2)}` : '-',
    },
    { title: '报修时间', dataIndex: 'created_at', key: 'created_at', width: 160 },
    {
      title: '操作', key: 'actions', width: 160,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(r)}>
            详情
          </Button>
          {r.status === 'quoting' && (
            <Button size="small" type="primary"
              icon={<DollarOutlined />}
              onClick={() => { setSelectedRepair(r); setPayModalOpen(true); }}
            >
              去付款
            </Button>
          )}
          {(r.status === 'paid' || r.status === 'repairing') && (
            <Button size="small" danger
              icon={<StopOutlined />}
              onClick={() => { setSelectedRepair(r); setAbandonReason(''); setAbandonModalOpen(true); }}
            >
              放弃维修
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      {role === 'customer' ? (
        <>
          {/* 客户欢迎信息 */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 8, padding: '32px 24px', color: '#fff', marginBottom: 24,
          }}>
            <h2 style={{ color: '#fff', margin: 0, fontSize: 22 }}>
              欢迎回来，{(() => {
                try {
                  const info = JSON.parse(localStorage.getItem('admin_info') || '{}');
                  return info.realName || info.username || '客户';
                } catch { return '客户'; }
              })()}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0', fontSize: 14 }}>
              在这里可以查看您的维修工单进度
            </p>
          </div>

          {/* 客户统计卡片 — 不可点击 */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="我的维修工单"
                  value={stats.repairs}
                  prefix={<span style={{ color: '#1677ff', fontSize: 24 }}><ToolOutlined /></span>}
                />
              </Card>
            </Col>
          </Row>

          {/* 我的维修工单 */}
          <Card title="我的维修工单" style={{ marginTop: 24 }}>
            {recentRepairs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                暂无维修工单
              </div>
            ) : (
              <Table
                rowKey="id"
                columns={customerColumns}
                dataSource={recentRepairs}
                pagination={false}
                size="small"
                scroll={{ x: 800 }}
              />
            )}
          </Card>

          {/* ==================== 客户弹窗：工单详情 ==================== */}
          <Modal
            title={`工单详情 - ${selectedRepair?.repair_no || ''}`}
            open={detailModalOpen}
            onCancel={() => { setDetailModalOpen(false); setRepairDetailData(null); }}
            footer={
              <Space>
                <Button onClick={() => { setDetailModalOpen(false); setRepairDetailData(null); }}>关闭</Button>
                {(repairDetailData?.status === 'quoting' || selectedRepair?.status === 'quoting') && (
                  <Button type="primary" icon={<DollarOutlined />}
                    onClick={() => {
                      const rid = repairDetailData?.id || selectedRepair?.id;
                      const rno = repairDetailData?.repair_no || selectedRepair?.repair_no;
                      setSelectedRepair({ id: rid, repair_no: rno, ...repairDetailData });
                      setPayModalOpen(true);
                    }}
                  >
                    去付款
                  </Button>
                )}
                {((repairDetailData?.status === 'paid' || repairDetailData?.status === 'repairing') ||
                  (selectedRepair?.status === 'paid' || selectedRepair?.status === 'repairing')) && (
                  <Button danger icon={<StopOutlined />}
                    onClick={() => {
                      const rid = repairDetailData?.id || selectedRepair?.id;
                      setSelectedRepair({ id: rid, ...repairDetailData });
                      setAbandonReason('');
                      setAbandonModalOpen(true);
                    }}
                  >
                    放弃维修
                  </Button>
                )}
              </Space>
            }
            width={640}
            destroyOnClose
          >
            <Spin spinning={detailLoading}>
              {repairDetailData ? (
                <>
                  <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="单号">{repairDetailData.repair_no}</Descriptions.Item>
                    <Descriptions.Item label="状态">
                      <Tag color={statusColorMap[repairDetailData.status]}>{statusTextMap[repairDetailData.status]}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="客户名">{repairDetailData.customer_name}</Descriptions.Item>
                    <Descriptions.Item label="电话">{repairDetailData.phone}</Descriptions.Item>
                    <Descriptions.Item label="回寄地址" span={2}>{repairDetailData.return_address || '-'}</Descriptions.Item>
                    <Descriptions.Item label="故障描述" span={2}>{repairDetailData.fault_desc || '-'}</Descriptions.Item>
                    <Descriptions.Item label="SN号">{repairDetailData.sn_number || '-'}</Descriptions.Item>
                    <Descriptions.Item label="创建时间">{repairDetailData.created_at || '-'}</Descriptions.Item>
                  </Descriptions>

                  {(repairDetailData.detection_fee > 0 || repairDetailData.material_fee > 0 || repairDetailData.postage > 0) && (
                    <>
                      <Divider orientation="left" plain style={{ fontSize: 13 }}>费用明细</Divider>
                      <Descriptions column={3} size="small" bordered>
                        {repairDetailData.detection_fee > 0 && (
                          <Descriptions.Item label="检测费">¥{parseFloat(repairDetailData.detection_fee).toFixed(2)}</Descriptions.Item>
                        )}
                        {repairDetailData.material_fee > 0 && (
                          <Descriptions.Item label="材料费">¥{parseFloat(repairDetailData.material_fee).toFixed(2)}</Descriptions.Item>
                        )}
                        {repairDetailData.postage > 0 && (
                          <Descriptions.Item label="邮费">¥{parseFloat(repairDetailData.postage).toFixed(2)}</Descriptions.Item>
                        )}
                        {repairDetailData.discount_amount > 0 && (
                          <Descriptions.Item label="VIP折扣">-¥{parseFloat(repairDetailData.discount_amount).toFixed(2)}</Descriptions.Item>
                        )}
                        <Descriptions.Item label="合计">
                          <strong style={{ color: '#f5222d', fontSize: 15 }}>¥{parseFloat(repairDetailData.total_fee).toFixed(2)}</strong>
                        </Descriptions.Item>
                      </Descriptions>
                    </>
                  )}

                  {repairDetailData.express_no && (
                    <>
                      <Divider orientation="left" plain style={{ fontSize: 13 }}>物流信息</Divider>
                      <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="快递公司">{repairDetailData.express_company || '-'}</Descriptions.Item>
                        <Descriptions.Item label="运单号">{repairDetailData.express_no}</Descriptions.Item>
                      </Descriptions>
                    </>
                  )}

                  {repairDetailData.payRecords && repairDetailData.payRecords.length > 0 && (
                    <>
                      <Divider orientation="left" plain style={{ fontSize: 13 }}>支付记录</Divider>
                      <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        columns={[
                          { title: '流水号', dataIndex: 'transaction_id', width: 180 },
                          { title: '金额', dataIndex: 'amount', render: (v) => `¥${v}` },
                          { title: '方式', dataIndex: 'pay_method', render: (v) => ({ cash: '现金', scan: '当面扫码', transfer: '转账', bank: '银行转账', other: '其他', wechat_pay: '微信支付', alipay: '支付宝', allinpay: '通联支付', admin_web: '后台代付' })[v] || v },
                          { title: '时间', dataIndex: 'created_at' },
                        ]}
                        dataSource={repairDetailData.payRecords}
                      />
                    </>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无数据</div>
              )}
            </Spin>
          </Modal>

          {/* ==================== 客户弹窗：付款引导（模拟支付已下线） ==================== */}
          <Modal
            title={<Space><DollarOutlined />付款引导</Space>}
            open={payModalOpen}
            onCancel={() => setPayModalOpen(false)}
            width={420}
            footer={[
              <Button key="ok" type="primary" onClick={handlePay}>
                我知道了
              </Button>,
            ]}
            destroyOnClose
          >
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <p style={{ fontSize: 15, marginBottom: 8 }}>
                工单：<strong>{selectedRepair?.repair_no || ''}</strong>
              </p>
              <p style={{ fontSize: 24, color: '#f5222d', fontWeight: 'bold', margin: '12px 0' }}>
                ¥{selectedRepair?.total_fee ? parseFloat(selectedRepair.total_fee).toFixed(2) : '0.00'}
              </p>
              <div style={{
                background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6,
                padding: '12px 14px', textAlign: 'left', margin: '12px 0 0',
              }}>
                <p style={{ color: '#ad6800', fontSize: 13, margin: 0, lineHeight: 1.9 }}>
                  此工单需在线支付。请打开微信搜索「嘉宇服务平台」小程序，
                  进入「我的 → 维修工单」找到本工单，点击「去支付」完成付款，
                  维修人员将尽快处理。
                </p>
              </div>
            </div>
          </Modal>

          {/* ==================== 客户弹窗：放弃维修 ==================== */}
          <Modal
            title={<Space><ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />放弃维修确认</Space>}
            open={abandonModalOpen}
            onOk={handleAbandon}
            onCancel={() => { setAbandonModalOpen(false); setAbandonReason(''); }}
            confirmLoading={abandonLoading}
            okText="确认放弃"
            cancelText="再想想"
            okButtonProps={{ danger: true }}
            width={480}
            destroyOnClose
          >
            <Result
              status="warning"
              title="确定要放弃此工单的维修吗？"
              subTitle={selectedRepair?.repair_no ? `工单号：${selectedRepair.repair_no}` : ''}
              style={{ padding: '12px 0' }}
            />
            <div style={{
              background: '#fff2f0', border: '1px solid #ffccc7',
              borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 13,
            }}>
              <p style={{ margin: 0, color: '#cf1322' }}>⚠️ 放弃维修后：</p>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20, color: '#cf1322' }}>
                <li>检测费（已产生）不予退还</li>
                <li>邮费/材料费将退还给您</li>
                <li>客服将联系您确认退款事宜</li>
                <li>工单状态将变为"已取消"</li>
              </ul>
            </div>
            <div style={{ marginTop: 12 }}>
              <p style={{ marginBottom: 4, fontSize: 13, color: '#666' }}>放弃原因（选填）：</p>
              <textarea
                placeholder="请简要说明放弃维修的原因..."
                value={abandonReason}
                onChange={(e) => setAbandonReason(e.target.value)}
                style={{
                  width: '100%', minHeight: 60, padding: 8, fontSize: 13,
                  border: '1px solid #d9d9d9', borderRadius: 4, resize: 'vertical',
                }}
                maxLength={200}
              />
            </div>
          </Modal>
        </>
      ) : (
        <>
          {/* 管理员视图 — 原版 */}
          <Row gutter={[16, 16]}>
            {statCards.map((card, idx) => (
              <Col xs={24} sm={12} lg={6} key={idx}>
                <Card hoverable onClick={() => navigate(card.path)}>
                  <Statistic
                    title={card.title}
                    value={card.value}
                    prefix={<span style={{ color: card.color, fontSize: 24 }}>{card.icon}</span>}
                  />
                </Card>
              </Col>
            ))}
          </Row>

          <Card title="最近维修工单" style={{ marginTop: 24 }} extra={<a onClick={() => navigate('/repair/list')}>查看全部</a>}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={recentRepairs}
              pagination={false}
              size="small"
              onRow={(record) => ({
                onClick: () => navigate(`/repair/detail/${record.id}`),
                style: { cursor: 'pointer' },
              })}
            />
          </Card>
        </>
      )}
    </Spin>
  );
}

export default Dashboard;
